import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { array, fn } from '@ember/helper';
import { or } from 'tio-ui/utilities';
import ReSelect from 'tio-reimagined/components/select';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import { PhX } from 'ember-phosphor-icons';
import {
  today,
  hoursAgo,
  daysAgo,
  weeksAgo,
  monthsAgo,
  quartersAgo,
  yearsAgo,
} from '../../utils/date/index.ts';

/**
 * Relative date range preset configuration
 */
export interface RelativeDatePreset {
  id: string;
  label: string;
  getDates: () => { start: string; end: string };
}

/**
 * Available relative date range presets
 */
export const RELATIVE_DATE_PRESETS: RelativeDatePreset[] = [
  {
    id: 'past-24-hours',
    label: 'Past 24 Hours',
    getDates: () => ({ start: hoursAgo(24), end: today() }),
  },
  {
    id: 'past-7-days',
    label: 'Past 7 Days',
    getDates: () => ({ start: daysAgo(7), end: today() }),
  },
  {
    id: 'past-30-days',
    label: 'Past 30 Days',
    getDates: () => ({ start: daysAgo(30), end: today() }),
  },
  {
    id: 'past-week',
    label: 'Past Week',
    getDates: () => ({ start: weeksAgo(1), end: today() }),
  },
  {
    id: 'past-month',
    label: 'Past Month',
    getDates: () => ({ start: monthsAgo(1), end: today() }),
  },
  {
    id: 'past-3-months',
    label: 'Past 3 Months',
    getDates: () => ({ start: monthsAgo(3), end: today() }),
  },
  {
    id: 'past-6-months',
    label: 'Past 6 Months',
    getDates: () => ({ start: monthsAgo(6), end: today() }),
  },
  {
    id: 'past-quarter',
    label: 'Past Quarter',
    getDates: () => ({ start: quartersAgo(1), end: today() }),
  },
  {
    id: 'past-year',
    label: 'Past Year',
    getDates: () => ({ start: yearsAgo(1), end: today() }),
  },
  {
    id: 'custom',
    label: 'Custom',
    getDates: () => ({ start: '', end: '' }),
  },
];

/**
 * Date range picker with relative date presets (Past 24 Hours, Past 7 Days, etc.)
 *
 * This component wraps the standard DateRangePicker and adds quick-select buttons
 * for common relative date ranges. When a preset is selected, the component:
 * 1. Calculates the date range based on today's date
 * 2. Updates both the start and end dates
 * 3. Stores the preset ID for serialization
 *
 * When a saved report is loaded with a relative preset, the dates are automatically
 * recalculated based on the current date.
 *
 * @example
 * <Forms::RelativeDateRangePicker
 *   @label="Application Creation Date"
 *   @startDate={{@applicationCreatedAtStart}}
 *   @endDate={{@applicationCreatedAtEnd}}
 *   @selectedPreset={{@applicationDateRangePreset}}
 *   @onDateRangeChange={{this.handleDateRangeChange}}
 *   @disabled={{not @showIndividualApplications}}
 * />
 */
export interface RelativeDateRangePickerSignature {
  Args: {
    /** Label for the date range */
    label?: string;

    /** Start date value (ISO string or empty) */
    startDate?: string;

    /** End date value (ISO string or empty) */
    endDate?: string;

    /** Currently selected preset ID (e.g., 'past-3-months', 'custom') */
    selectedPreset?: string;

    /**
     * Callback when date range changes
     * Receives an object with: startDate, endDate, and presetId
     */
    onDateRangeChange: (update: { startDate: string; endDate: string; presetId: string }) => void;

    /** Whether the inputs are disabled */
    disabled?: boolean;

    /** Custom presets (optional - defaults to RELATIVE_DATE_PRESETS) */
    presets?: RelativeDatePreset[];
  };
  Element: HTMLDivElement;
}

export default class RelativeDateRangePickerComponent extends Component<RelativeDateRangePickerSignature> {
  get presets(): RelativeDatePreset[] {
    return this.args.presets ?? RELATIVE_DATE_PRESETS;
  }

  get presetSelectItems() {
    return this.presets.map((preset) => ({
      key: preset.id,
      label: preset.label,
    }));
  }

  get activePresetId(): string {
    return this.args.selectedPreset ?? 'custom';
  }

  get isCustomMode(): boolean {
    return this.activePresetId === 'custom';
  }

  /**
   * Select a relative date preset from dropdown
   */
  handlePresetChange = (presetId: string | null) => {
    if (!presetId) return;

    const preset = this.presets.find((p) => p.id === presetId);
    if (!preset) return;

    const { start, end } = preset.getDates();

    this.args.onDateRangeChange({
      startDate: start,
      endDate: end,
      presetId: presetId,
    });
  };

  /**
   * Handle manual start date change (switches to custom mode)
   */
  handleStartDateChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString: string = date ? (date.toISOString().split('T')[0] ?? '') : '';

    this.args.onDateRangeChange({
      startDate: dateString,
      endDate: this.args.endDate || '',
      presetId: 'custom',
    });
  };

  /**
   * Handle manual end date change (switches to custom mode)
   */
  handleEndDateChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString: string = date ? (date.toISOString().split('T')[0] ?? '') : '';

    this.args.onDateRangeChange({
      startDate: this.args.startDate || '',
      endDate: dateString,
      presetId: 'custom',
    });
  };

  <template>
    <div class="tio-col" ...attributes>
      {{! Header with Label and Preset Dropdown }}
      <div class="tio-row-between gap-4">
        {{#if @label}}
          <label class="text-sm font-medium tio-fg-primary">{{@label}}</label>
        {{/if}}
        <div class="shrink-0 w-48">
          <ReSelect
            @items={{this.presetSelectItems}}
            @selected={{this.activePresetId}}
            @onChange={{this.handlePresetChange}}
            @size="sm"
            @disabled={{@disabled}}
            @clearable={{false}}
          />
        </div>
      </div>

      {{! Date Range Picker (inline) }}
      <div class="tio-col-tight">

        <div class="tio-row-tight">
          <span class="text-sm tio-fg-secondary">From</span>

          <div class="flex-1 relative">
            {{! @glint-expect-error: return to this and follow up with dev}}
            <EmberFlatpickr
              @date={{or @startDate ""}}
              @onChange={{this.handleStartDateChange}}
              @dateFormat="Y-m-d"
              @altInput={{true}}
              @altFormat="F j, Y"
              placeholder="mm/dd/yyyy"
              disabled={{@disabled}}
              class="tio-form-input"
            />
            {{#if @startDate}}
              <button
                type="button"
                {{on "click" (fn this.handleStartDateChange (array))}}
                disabled={{@disabled}}
                class="absolute right-2 top-1/2 -translate-y-1/2 tio-fg-disabled hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Clear start date"
              >
                <PhX @size="16" />
              </button>
            {{/if}}
          </div>

          <span class="text-sm tio-fg-secondary">To</span>

          <div class="flex-1 relative">
            {{! @glint-expect-error: return to this and follow up with dev}}
            <EmberFlatpickr
              @date={{or @endDate ""}}
              @onChange={{this.handleEndDateChange}}
              @dateFormat="Y-m-d"
              @altInput={{true}}
              @altFormat="F j, Y"
              @minDate={{or @startDate ""}}
              placeholder="mm/dd/yyyy"
              disabled={{@disabled}}
              class="tio-form-input"
            />
            {{#if @endDate}}
              <button
                type="button"
                {{on "click" (fn this.handleEndDateChange (array))}}
                disabled={{@disabled}}
                class="absolute right-2 top-1/2 -translate-y-1/2 tio-fg-disabled hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Clear end date"
              >
                <PhX @size="16" />
              </button>
            {{/if}}
          </div>
        </div>
      </div>
    </div>
  </template>
}
